import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { AppCustomPreloader } from './app-routing-preloader';
import { BigcommerceInstallationGuideComponent } from './bigcommerce-installation-guide/bigcommerce-installation-guide.component';
import { GmailLandingComponent } from './shared-components/gmail-landing/gmail-landing.component';
import { GoogleAccountLandingComponent } from './shared-components/google-account-landing/google-account-landing.component';
import { NoResourceComponent } from './shared/404/no-resource.component';
import { billingOwnerGuard } from './shared/guards/billing-owner.guard';
import { signedInGuard } from './shared/guards/signed-in.guard';
import { SmiV2Guard } from './shared/guards/smi-v2.guard';
import { SidenavGroupComponent } from './sidenav/sidenav/sidenav-group.component';
import { SocialMediaInfluencerModule } from './social-media-influencer/social-media-influencer.module';
import { UserSprintModule } from './user-management/new-sprint/user-sprint.module';
import { ReportPreviewExportPageComponent } from './social-media-influencer/modules/social-listening/pages/reports/pages/report-preview-export-page/report-preview-export-page.component';
import { AiSummaryExportComponent } from './social-media-influencer/modules/social-listening/pages/ai-summary/ai-summary-export.component';
import { FindMentionsExportComponent } from './social-media-influencer/modules/social-listening/pages/find-mentions/find-mentions-export.component';
import { DiscoveryProfileExportComponent } from './social-media-influencer/modules/discovery-v2/components/discovery-profile/pages/discovery-profile-export.component';

// A bare page that takes the assistant conversation, mounted inside the shell and
// ONLY where the e2e environment opts in. It is how "one surface draws the conversation" is proved
// before a product page that hosts it exists — the refactor behind that rule can
// break Ana application-wide, so it is verified where nothing else is moving.
const anaChatSlotProbeRoute: Routes = environment.Flags.anaChatSlotProbe
	? [
			{
				path: 'ana-chat-slot-probe',
				loadComponent: () =>
					import(
						'./shared/ana-chat-slot-probe/ana-chat-slot-probe.page'
					).then(m => m.AnaChatSlotProbePage)
			}
	  ]
	: [];

const routes: Routes = [
	{
		path: 'social-listening/reports/preview/:reportId/export',
		component: ReportPreviewExportPageComponent
	},
	{
		// A report's view-only link. No guard, outside the shell, like the
		// export route above: it is opened by people with no Lolly account,
		// and the token in the path is the only credential it needs.
		path: 'social-listening/reports/shared/:token',
		loadComponent: () =>
			import(
				'./social-media-influencer/modules/social-listening/pages/reports/pages/report-shared-page/report-shared-page.component'
			).then(m => m.ReportSharedPageComponent)
	},
	{
		path: 'authentication',
		loadChildren: () =>
			import('./authentication-v2/authentication-v2.module').then(
				m => m.AuthenticationV2Module
			)
	},
	{
		path: 'social-listening/ai-summary/:summaryId/export',
		component: AiSummaryExportComponent
	},
	{
		// Headless print page for Find mentions' Download PDF (Gotenberg); the
		// export token in the query is its only credential.
		path: 'social-listening/find/:searchId/export',
		component: FindMentionsExportComponent
	},
	{
		// Headless print page for the Discovery-slider PDF export (Gotenberg).
		path: 'discovery-profile/:discoveryId/export/:platform',
		component: DiscoveryProfileExportComponent
	},
	{
		// Feature onboarding funnels — full-screen, outside the app shell.
		// Each feature adds a child path here (e.g. `onboarding/affiliates`).
		path: 'onboarding/affiliates',
		canActivate: [signedInGuard],
		loadChildren: () =>
			import(
				'./social-media-influencer/modules/affiliates/onboarding/affiliate-onboarding.routes'
			).then(m => m.AFFILIATE_ONBOARDING_ROUTES)
	},
	{
		path: 'onboarding/social-listening',
		canActivate: [signedInGuard],
		loadChildren: () =>
			import(
				'./social-media-influencer/modules/social-listening/onboarding/social-listening-onboarding.routes'
			).then(m => m.SOCIAL_LISTENING_ONBOARDING_ROUTES)
	},
	{
		// `onboarding/discovery`, `onboarding/partner-ads` and
		// `onboarding/store-install` — the three funnels built from steps that
		// already existed. They share every step component they use, so they
		// share one lazy chunk.
		path: 'onboarding',
		canActivate: [signedInGuard],
		loadChildren: () =>
			import('./onboarding/funnels/department-funnels.routes').then(
				m => m.ONBOARDING_DEPARTMENT_FUNNEL_ROUTES
			)
	},
	{
		// OAuth consent for MCP clients. The backend's authorization endpoint
		// validates the client and redirects the browser here with one opaque
		// signed `request` — `PUBLIC_API_MCP_OAUTH_CONSENT_URL` defaults to
		// this app's origin + `/oauth/consent`, so this route is part of the
		// authorization flow, not an optional page. Full screen, outside the
		// app shell, and declared before the shell route so the empty-path
		// child tree never gets a chance to claim it.
		path: 'oauth/consent',
		canActivate: [signedInGuard],
		loadComponent: () =>
			import('./oauth/consent/oauth-consent-page.component').then(
				m => m.OAuthConsentPageComponent
			)
	},
	{
		path: '',
		component: SidenavGroupComponent,
		canActivate: [SmiV2Guard],
		children: [
			// Inside the shell on purpose: the rule it proves is enforced BY the
			// shell — the panel is not shown and the Ask Ana control opens nothing
			// while a page holds the conversation. A probe mounted outside it would
			// have exercised none of that.
			...anaChatSlotProbeRoute,
			{
				path: 'user-management',
				loadChildren: () => UserSprintModule
			},
			{
				// No shell-wide subscription gate. It used to carry
				// `anySubscriptionGuard`, which bounced every product page to
				// the plans page behind a "your subscription has ended" dialog
				// whenever `GET /billing` came back empty. Under the unified
				// catalogue an empty billing read is the FREE TIER — nothing
				// grants a subscription on `application_free`; a free brand gets
				// a credit account and an allowance instead — so that guard
				// locked the whole app for the tier this platform sells hardest.
				path: '',
				loadChildren: () => SocialMediaInfluencerModule
			}
		]
	},
	{
		path: 'pricing',
		loadComponent: () =>
			import('./billing/pricing/pricing-page.component').then(
				m => m.PricingPageComponent
			)
	},
	{
		path: 'thank-you',
		loadComponent: () =>
			import('./billing/thank-you/thank-you-page.component').then(
				m => m.ThankYouPageComponent
			)
	},
	{
		// Admin-shared checkout links land here; the page forwards the
		// signed-in visitor straight to Stripe (anonymous → login first).
		// Owner-only for the same reason the billing page is: it starts a
		// checkout against the account's payment relationship.
		path: 'subscribe',
		canActivate: [signedInGuard, billingOwnerGuard],
		loadComponent: () =>
			import('./billing/subscribe/subscribe-page.component').then(
				m => m.SubscribePageComponent
			)
	},
	{
		path: 'bigcommerce/installation-guide',
		component: BigcommerceInstallationGuideComponent
	},
	{ path: '404', component: NoResourceComponent },
	{ path: 'google-landing', component: GoogleAccountLandingComponent },
	{ path: 'gmailAuth', component: GmailLandingComponent },
	{ path: '**', redirectTo: '404' }
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			useHash: false,
			preloadingStrategy: AppCustomPreloader
		})
	],
	exports: [RouterModule]
})
export class AppRoutingModule {}
